import React, { useState, useCallback, useRef } from 'react'
import { Box, Typography, Button } from '@mui/material'
import GetAppIcon from '@mui/icons-material/GetApp'
import { formatHandleTimeSeconds } from '../../services/format'
import { formatCallDateET } from '../../services/date'
import useQASummaryData from '../../hooks/useQASummaryData'
import QASummaryFilters from '../../components/QASummary/QASummaryFilters'
import QASummaryStats from '../../components/QASummary/QASummaryStats'
import QASummaryTable from '../../components/QASummary/QASummaryTable'
import QASummaryCharts from '../../components/QASummary/QASummaryCharts'
import ExportDialog from '../../components/QASummary/ExportDialog'
import { CallDrawerModern, PageLayout, PageHeader } from '../../components'
import { useDrawerURL, useToast } from '../../hooks'
import { authenticatedFetch } from '../../services/api/apiClient'
import { apiEndpoints } from '../../consts'
import logger from '../../utils/logger'
import { LoadingSpinner, ToastNotification } from '../../components/atoms'
import styles from './AdminQASummary.module.scss'

const AdminQASummary = () => {
  const qaSummaryData = useQASummaryData()

  const {
    filters,
    filterOptions,
    loadingFilters,
    updateFilters,
    clearFilters,
    hasActiveFilters,
    data,
    loading,
    error,
    page,
    limit,
    totalPages,
    total,
    handlePageChange,
    handleLimitChange,
    sortField,
    sortDirection,
    handleSort,
    exportCSV
  } = qaSummaryData

  const [selectedCall, setSelectedCall] = useState(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const openingFromURLRef = useRef(false)

  const [exportDialogOpen, setExportDialogOpen] = useState(false)
  const [exporting, setExporting] = useState(false)

  const { toast, showToast, closeToast } = useToast()

  const transformCallForDrawer = (call) => {
    if (!call) return null

    const formatHandleTime = formatHandleTimeSeconds

    const phoneNumber = call.direction === 'Inbound'
      ? (call.callerNumber || call.dnis || 'N/A')
      : (call.dnis || call.callerNumber || 'N/A')

    return {
      id: call.callId,
      0: formatCallDateET(call.callDate, { options: { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' } }),
      1: phoneNumber,
      4: call.direction || 'Inbound',
      5: call.campaignName || 'Unknown',
      6: call.agentName || 'Unknown',
      9: formatHandleTime(call.handleTime),
      13: call.disposition || 'Unknown',
      14: call.callNotes || '',
      17: call.callerName || '',
      18: call.callerCompany || '',
      caller_email: call.callerEmail || '',
      ai_transcript: call.aiTranscript,
      ai_summary: call.aiSummary,
      sentiment_analysis: call.sentimentAnalysis,
      qa_evaluation: call.qaEvaluation,
      call_id: call.callId,
      campaign_name: call.campaignName,
      agent_name: call.agentName,
      handle_time: call.handleTime,
      call_date: call.callDate
    }
  }

  const handleOpenFromURL = useCallback(async (callId) => {
    openingFromURLRef.current = true
    try {
      const resp = await authenticatedFetch(
        `${apiEndpoints.calls}?callId=${encodeURIComponent(callId)}&limit=1`
      )
      if (!resp.ok) return
      const json = await resp.json()
      const calls = json.calls || json.data
      if (!calls?.length) return

      const call = calls[0]
      const camelCall = {
        callId: call.call_id,
        callDate: call.call_date,
        direction: call.direction,
        callerNumber: call.caller_number,
        dnis: call.dnis,
        campaignName: call.campaign_name,
        agentName: call.agent_name,
        handleTime: call.handle_time,
        disposition: call.disposition,
        callNotes: call.call_notes,
        callerName: call.caller_name,
        callerCompany: call.caller_company,
        callerEmail: call.caller_email,
        aiTranscript: call.ai_transcript,
        aiSummary: call.ai_summary,
        sentimentAnalysis: call.sentiment_analysis,
        qaEvaluation: call.qa_evaluation
      }
      setSelectedCall(transformCallForDrawer(camelCall))
      setDrawerOpen(true)
    } catch (err) {
      logger.error('Error opening QA call from URL:', err)
    } finally {
      openingFromURLRef.current = false
    }
  }, [])

  const { openDrawerWithURL, closeDrawerWithURL } = useDrawerURL('qa', {
    drawerOpen,
    onOpenFromURL: handleOpenFromURL,
    onClose: () => { setDrawerOpen(false); setSelectedCall(null) }
  })

  const handleRowClick = (call) => {
    setSelectedCall(transformCallForDrawer(call))
    setDrawerOpen(true)
    if (!openingFromURLRef.current) {
      openDrawerWithURL(call.callId)
    }
  }

  const handleDrawerClose = () => {
    setDrawerOpen(false)
    setSelectedCall(null)
    closeDrawerWithURL()
  }

  const handleOpenExportDialog = () => {
    setExportDialogOpen(true)
  }

  const handleCloseExportDialog = () => {
    setExportDialogOpen(false)
  }

  const handleExportWithOptions = async (options) => {
    try {
      setExporting(true)
      await exportCSV(options)
      setExportDialogOpen(false)
    } catch (err) {
      logger.error('Error exporting QA summary CSV:', err)
      showToast('Failed to export CSV. Please try again.', 'error')
    } finally {
      setExporting(false)
    }
  }

  if (error) {
    return (
      <PageLayout>
        <div className={styles.errorContainer}>
          <Typography variant="h6" className={styles.errorTitle}>Error loading QA summary</Typography>
          <Typography variant="body2" className={styles.errorMessage}>{error}</Typography>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout>
      <PageHeader
        title="QA Summary"
        subtitle="View and analyze QA evaluation results across all calls"
        actions={
          <Button
            variant="contained"
            className={styles.exportButton}
            startIcon={<GetAppIcon />}
            onClick={handleOpenExportDialog}
            disabled={loading || data.calls.length === 0}
          >
            Export CSV
          </Button>
        }
      />

      {/* Main Content */}
      <div className={styles.content}>
        {/* Filters */}
        <Box className={styles.section}>
          <QASummaryFilters
            filters={filters}
            filterOptions={filterOptions}
            loading={loadingFilters}
            onFilterChange={updateFilters}
            onClearFilters={clearFilters}
            hasActiveFilters={hasActiveFilters()}
          />
        </Box>

        {loading && data.calls.length === 0 ? (
          <div className={styles.loadingContainer}>
            <LoadingSpinner />
          </div>
        ) : (
          <>
            {/* Statistics */}
            <Box className={styles.section}>
              <QASummaryStats statistics={data.statistics} loading={loading} />
            </Box>

            {/* Charts */}
            <Box className={styles.section}>
              <QASummaryCharts
                chartData={data.chartData}
                statistics={data.statistics}
                loading={loading}
              />
            </Box>

            {/* Results Table */}
            <Box className={styles.section}>
              <QASummaryTable
                calls={data.calls}
                loading={loading}
                page={page}
                totalPages={totalPages}
                total={total}
                limit={limit}
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
                onPageChange={handlePageChange}
                onLimitChange={handleLimitChange}
                onRowClick={handleRowClick}
              />
            </Box>
          </>
        )}
      </div>

      {/* Call Detail Drawer - Modern Card Layout */}
      <CallDrawerModern
        selectedRow={selectedCall}
        drawerOpen={drawerOpen}
        handleCloseDrawerInternal={handleDrawerClose}
        currentVote={null}
        onUpvote={() => {}}
        onDownvote={() => {}}
      />

      {/* Export Dialog */}
      <ExportDialog
        open={exportDialogOpen}
        onClose={handleCloseExportDialog}
        onExport={handleExportWithOptions}
        currentFilters={{
          startDate: filters.startDate,
          endDate: filters.endDate
        }}
        totalResults={total}
        loading={exporting}
      />

      <ToastNotification
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={closeToast}
      />
    </PageLayout>
  )
}

export default AdminQASummary
